import { useState, useRef, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  DateField,
  Dropdown,
  HelpDrawer,
  HelpDrawerToggle,
  TextField,
  unmaskValue,
} from '@cmsgov/ds-healthcare-gov';
import { getDateString } from '../../../utilities/date';
import { runValidations, FieldValidation } from '../../../utilities/validation';
import { validators } from '../../../utilities/validators';
import { frequencyAmountToIncome } from '../../../utilities/incomeCalculations';
import { frequencyTypes, frequencyLabel, FrequencyType } from '../../../common/enums/FrequencyTypes';
import { incomeTypes, incomeLabel, IncomeType } from '../../../common/enums/IncomeTypes';
import type { Income } from '../IncomeCalculator';

export type DateObject = {
  month: string;
  day: string;
  year: string;
};

type Props = {
  amount?: string;
  incomeFrequency?: FrequencyType;
  incomeType?: IncomeType;
  onCancelAddIncome: () => void;
  onDoneClick: (incomeRecord: Income) => void;
  endDate?: DateObject;
  startDate?: DateObject;
};

const IncomeFields = (props: Props) => {
  const [amount, setAmount] = useState(props.amount ?? '');
  const [incomeFrequency, setIncomeFrequency] = useState<FrequencyType | ''>(props.incomeFrequency ?? '');
  const [incomeType, setIncomeType] = useState<IncomeType | ''>(props.incomeType ?? '');
  const [startDate, setStartDate] = useState<DateObject>(props.startDate || { month: '', day: '', year: '' });
  const [endDate, setEndDate] = useState<DateObject>(props.endDate || { month: '', day: '', year: '' });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showDrawer, setShowDrawer] = useState(false);
  const startDateRef = useRef<HTMLDivElement | null>(null);
  const endDateRef = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    const enableDates = !!incomeFrequency && incomeFrequency !== frequencyTypes.ONE_TIME;
    let startDateInputs: NodeListOf<HTMLInputElement> | Array<never> = [];
    if (startDateRef.current) {
      startDateInputs = startDateRef.current.querySelectorAll('input');
    }
    let endDateInputs: NodeListOf<HTMLInputElement> | Array<never> = [];
    if (endDateRef.current) {
      endDateInputs = endDateRef.current.querySelectorAll('input');
    }

    [...startDateInputs, ...endDateInputs].forEach((x) => {
      x.disabled = !enableDates;
    });
  }, [incomeFrequency]);

  const handleUpdateField = async (fieldName: string, value: string, blur: boolean = false) => {
    switch (fieldName) {
      case 'incomeType':
        setIncomeType(value as IncomeType);
        break;
      case 'amount':
        setAmount(unmaskValue(value, 'currency'));
        if (blur) {
          await validate('amount');
        }
        break;
      case 'incomeFrequency': {
        const blankDate = { month: '', day: '', year: '' } as const;

        const newErrors: Record<string, any> = {};
        Object.keys(formErrors).forEach((key) => {
          if (key !== 'startDate' && key !== 'endDate') {
            newErrors[key] = formErrors[key];
          }
        });

        setIncomeFrequency(value as FrequencyType);
        setStartDate(blankDate);
        setEndDate(blankDate);
        setFormErrors(newErrors);
        break;
      }
      default:
        break;
    }
    if (fieldName !== 'amount' && formErrors[fieldName]) {
      await validate(fieldName);
    }
  };

  const handleDateUpdateField = async (fieldName: string, value: DateObject) => {
    switch (fieldName) {
      case 'startDate':
        setStartDate(value);
        break;
      case 'endDate':
        setEndDate(value);
        break;
      default:
        throw new Error('Not a valid field name');
    }
    if (formErrors[fieldName]) {
      await validate(fieldName);
    }
  };

  const getValidations = (): Array<FieldValidation> => {
    const validations: Array<FieldValidation> = [];
    const {
      betweenNegMillionAndPosMillion,
      between0andMillion,
      endDateInvalid,
      frequencyRequired,
      incomeTypeRequired,
      startDateInvalid,
    } = validators;
    const startDateString = getDateString(startDate) ?? '';
    const endDateString = getDateString(endDate) ?? '';
    const allowsNegativeIncome = [
      incomeTypes.SELF_EMPLOYMENT,
      incomeTypes.CAPITAL_GAINS,
      incomeTypes.FARMING_OR_FISHING_INCOME,
      incomeTypes.RENTAL_OR_ROYALTY_INCOME,
    ];

    validations.push(['incomeType', incomeTypeRequired, incomeType]);
    // @ts-expect-error incomeType can be '' which is not assignable to the includes method
    if (allowsNegativeIncome.includes(incomeType)) {
      validations.push(['amount', betweenNegMillionAndPosMillion, amount]);
    } else {
      validations.push(['amount', between0andMillion, amount]);
    }
    validations.push(['incomeFrequency', frequencyRequired, incomeFrequency]);
    if (shouldValidateDateFields()) {
      validations.push(['startDate', startDateInvalid, { value: startDateString, endDate: endDateString }]);
      validations.push(['endDate', endDateInvalid, { value: endDateString, startDate: startDateString }]);
    }
    return validations;
  };

  const validate = async (fieldName?: string | Array<string>) => {
    const { errors } = await runValidations(getValidations(), formErrors, fieldName);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDoneClick = async () => {
    if (await validate()) {
      const calculatedTotal = frequencyAmountToIncome(incomeFrequency, amount, startDate, endDate);
      if (incomeType && incomeFrequency) {
        props.onDoneClick({ amount, incomeType, incomeFrequency, startDate, endDate, calculatedTotal });
      }
    }
  };

  const handleCancelClick = () => {
    props.onCancelAddIncome();
  };

  const shouldValidateDateFields = () => {
    return (
      incomeFrequency === frequencyTypes.WEEKLY ||
      incomeFrequency === frequencyTypes.MONTHLY ||
      incomeFrequency === frequencyTypes.YEARLY
    );
  };

  const dropdownOptions: Record<'label' | 'options', any>[] = [
    {
      label: '',
      options: [
        { value: incomeTypes.JOB, label: t(incomeLabel.JOB) },
        { value: incomeTypes.SELF_EMPLOYMENT, label: t(incomeLabel.SELF_EMPLOYMENT) },
        { value: incomeTypes.UNEMPLOYMENT, label: t(incomeLabel.UNEMPLOYMENT) },
        { value: incomeTypes.SOCIAL_SECURITY_BENEFIT, label: t(incomeLabel.SOCIAL_SECURITY_BENEFIT) },
        { value: incomeTypes.RETIREMENT, label: t(incomeLabel.RETIREMENT) },
        { value: incomeTypes.PENSION, label: t(incomeLabel.PENSION) },
        { value: incomeTypes.INVESTMENT_INCOME, label: t(incomeLabel.INVESTMENT_INCOME) },
        { value: incomeTypes.CAPITAL_GAINS, label: t(incomeLabel.CAPITAL_GAINS) },
        { value: incomeTypes.RENTAL_OR_ROYALTY_INCOME, label: t(incomeLabel.RENTAL_OR_ROYALTY_INCOME) },
        { value: incomeTypes.FARMING_OR_FISHING_INCOME, label: t(incomeLabel.FARMING_OR_FISHING_INCOME) },
        { value: incomeTypes.ALIMONY_RECEIVED, label: t(incomeLabel.ALIMONY_RECEIVED) },
        { value: incomeTypes.SCHOLARSHIP, label: t(incomeLabel.SCHOLARSHIP) },
      ],
    },
    {
      label: t('moreIncomeSources'),
      options: [
        { value: incomeTypes.CANCELED_DEBT, label: t(incomeLabel.CANCELED_DEBT) },
        { value: incomeTypes.COURT_AWARDS, label: t(incomeLabel.COURT_AWARDS) },
        { value: incomeTypes.PRIZES_AWARDS_GAMBLING_WINNINGS, label: t(incomeLabel.PRIZES_AWARDS_GAMBLING_WINNINGS) },
        { value: incomeTypes.JURY_DUTY_PAY, label: t(incomeLabel.JURY_DUTY_PAY) },
        { value: incomeTypes.OTHER_INCOME, label: t(incomeLabel.OTHER_INCOME) },
      ],
    },
  ];
  if (incomeType === '') {
    // if no selection has been made, add a default option
    dropdownOptions[0].options.unshift({ value: '', label: t(incomeLabel.DEFAULT) });
  }

  return (
    <div className="ds-l-col--12">
      <div className="ds-l-row">
        <div className="ds-l-col--12 ds-l-md-col--4">
          <Dropdown
            name="income-type"
            label={t('incomeType')}
            options={dropdownOptions}
            onChange={(e) => handleUpdateField('incomeType', e.currentTarget.value)}
            onBlur={() => validate('incomeType')}
            value={incomeType}
            errorMessage={formErrors.incomeType}
          />
        </div>
        <div className="ds-l-col--12 ds-l-md-col--auto">
          <TextField
            ariaLabel="Enter Income amount in dollars."
            label={t('amount')}
            mask="currency"
            inputMode="numeric"
            type="text"
            name="amount"
            size="medium"
            onChange={(e) => handleUpdateField('amount', e.target.value)}
            onBlur={(e) => handleUpdateField('amount', e.target.value, true)} // blur will format the currency to two digits so we should update state
            value={amount}
            errorMessage={formErrors.amount}
          />
        </div>
        <div className="ds-l-col--12 ds-l-md-col--4">
          <Dropdown
            name="income-frequency"
            className="ds-u-padding--0"
            label={t('howOften')}
            size="medium"
            options={[
              { label: t(frequencyLabel.DEFAULT), value: '' },
              { label: t(frequencyLabel.WEEKLY), value: frequencyTypes.WEEKLY },
              { label: t(frequencyLabel.MONTHLY), value: frequencyTypes.MONTHLY },
              { label: t(frequencyLabel.YEARLY), value: frequencyTypes.YEARLY },
              { label: t(frequencyLabel.ONE_TIME), value: frequencyTypes.ONE_TIME },
            ]}
            onChange={(e) => handleUpdateField('incomeFrequency', e.currentTarget.value)}
            onBlur={() => validate('incomeFrequency')}
            value={incomeFrequency}
            errorMessage={formErrors.incomeFrequency}
          />
        </div>
        <div className="ds-l-col--12">
          <HelpDrawerToggle
            className="ds-u-margin-top--05"
            helpDrawerOpen={showDrawer}
            showDrawer={() => setShowDrawer(true)}
          >
            {t('helpEstimatingIncomeLink')}
          </HelpDrawerToggle>
          <HelpDrawer
            heading={t('helpEstimatingIncomeTitle')}
            isOpen={showDrawer}
            onCloseClick={() => setShowDrawer(false)}
            footerTitle={t('helpDrawerFooterTitle')}
            footerBody={
              // eslint-disable-next-line
              <Trans i18nKey={'helpDrawerFooterBody'} components={{ a: <a /> }} />
            }
          >
            <Trans i18nKey={'helpEstimatingIncomeBodyFPUC'} components={{ li: <li />, ul: <ul />, p: <p /> }} />
          </HelpDrawer>
        </div>
      </div>
      <div className="ds-l-row">
        <div className="ds-l-col--12">
          <div className="ds-text-heading--md ds-u-margin-top--3">{t('receiveIncome')}</div>
          <p>{t('receiveIncomeHint')}</p>
        </div>
        {incomeFrequency === frequencyTypes.ONE_TIME && (
          <div className="ds-l-row">
            <div className="ds-l-col--12 ds-l-md-col--8">
              <Alert className="ds-u-margin-top--3" hideIcon={true}>
                {t('oneTimeAlert')}
              </Alert>
            </div>
          </div>
        )}
      </div>
      <div className="ds-l-row">
        <div className="ds-l-md-col--4" ref={startDateRef}>
          <DateField
            label={t('incomeStartDate')}
            monthLabel={t('month')}
            dayLabel={t('day')}
            yearLabel={t('year')}
            onChange={(e, dateObj) => handleDateUpdateField('startDate', dateObj)}
            dayName="start-day"
            dayValue={startDate.day}
            monthName="start-month"
            monthValue={startDate.month}
            yearName="start-year"
            yearValue={startDate.year}
            dayInvalid={!!formErrors.startDate}
            monthInvalid={!!formErrors.startDate}
            yearInvalid={!!formErrors.startDate}
            errorMessage={formErrors.startDate}
            onComponentBlur={() => {
              if (!formErrors.endDate) {
                validate('startDate');
              } else {
                validate(['startDate', 'endDate']);
              }
            }}
          />
        </div>
        <div className="ds-l-md-col--4" ref={endDateRef}>
          <DateField
            label={t('incomeEndDate')}
            monthLabel={t('month')}
            dayLabel={t('day')}
            yearLabel={t('year')}
            onChange={(e, dateObj) => handleDateUpdateField('endDate', dateObj)}
            dayName="end-day"
            dayValue={endDate.day}
            monthName="end-month"
            monthValue={endDate.month}
            yearName="end-year"
            yearValue={endDate.year}
            dayInvalid={!!formErrors.endDate}
            monthInvalid={!!formErrors.endDate}
            yearInvalid={!!formErrors.endDate}
            errorMessage={formErrors.endDate}
            onComponentBlur={() => validate('endDate')}
          />
        </div>
      </div>
      <div className="ds-l-row ds-u-margin-top--3">
        <div className="ds-l-col--12">
          <Button
            variation="solid"
            className="ds-u-margin-right--2 ds-u-margin-bottom--2"
            onClick={handleDoneClick}
            aria-label={`${t('calculate')} ${t('income')}`}
          >
            {t('calculate')}
          </Button>
          <Button onClick={handleCancelClick} aria-label={`${t('cancel')} ${t('income')}`}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IncomeFields;
